import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import PropTypes from 'prop-types';

const Alert = React.forwardRef(function Alert (props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const SnackbarContext = createContext(null);

/**
 * Provides a single Snackbar surface for the whole app and exposes it via the
 * useSnackbar() hook. Replaces the old `setSnackbarProperties` prop-drilling.
 *
 *   const snackbar = useSnackbar();
 *   snackbar.success('Saved');
 *   snackbar.error('Could not save: ' + err);
 *   snackbar.show('Heads up', 'warning');
 */
export function SnackbarProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');

    const show = useCallback((nextMessage, nextSeverity) => {
        setMessage(nextMessage ?? '');
        setSeverity(nextSeverity ?? 'success');
        setOpen(true);
    }, []);

    // Compatibility shim for code that still calls `setSnackbarProperties(msg, severity)`.
    const setSnackbarProperties = show;

    const api = useMemo(() => ({
        show,
        setSnackbarProperties,
        success: (msg) => show(msg, 'success'),
        info: (msg) => show(msg, 'info'),
        warning: (msg) => show(msg, 'warning'),
        error: (msg) => show(msg, 'error'),
    }), [show, setSnackbarProperties]);

    const close = (event, reason) => {
        if (reason === 'clickaway') return;
        setOpen(false);
    };

    return (
        <SnackbarContext.Provider value={api}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={close}
                TransitionProps={{ onExited: () => { setMessage(''); setSeverity('success'); } }}
            >
                <Alert severity={severity} sx={{ width: '100%' }}>
                    {message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
}

SnackbarProvider.propTypes = {
    children: PropTypes.node,
};

export function useSnackbar() {
    const ctx = useContext(SnackbarContext);
    if (!ctx) {
        throw new Error('useSnackbar must be used inside a <SnackbarProvider>');
    }
    return ctx;
}
